'use client'

import Markdown from 'markdown-to-jsx'
import Link from 'next/link'
import React from 'react'
import { type Control } from 'react-hook-form'

import { Button } from '@/legacy-src/components/ui/button'
import { CheckboxCard } from '@/legacy-src/components/ui/checkbox'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/legacy-src/components/ui/form'
import { Input } from '@/legacy-src/components/ui/input'
import { RadioGroup, RadioGroupCard, RadioGroupItem } from '@/legacy-src/components/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/legacy-src/components/ui/select'
import { Textarea } from '@/legacy-src/components/ui/textarea'
import { getTranslate } from '@/legacy-src/utilities/i18n'

import { Checkbox } from '@/src/domains/design-system/components/Checkbox'
import { Label } from '@/src/domains/design-system/components/Label'

import { FieldType, type FieldConfig } from './types'

interface FormFieldRendererProps {
  field: FieldConfig
  control: Control<any>
  autoFocus?: boolean
}

export const FormFieldRenderer: React.FC<FormFieldRendererProps> = ({ field, control, autoFocus }) => {
  const { t } = getTranslate()

  const renderField = (field: FieldConfig) => {
    switch (field.fieldType) {
      case FieldType.BUTTONS_CHECKBOX:
        return (
          <FormField
            control={control}
            name={field.id}
            render={({ field: formField }) => (
              <FormItem>
                {field.label && <FormLabel isRequired={field.required}>{field.label}</FormLabel>}
                <FormControl>
                  <div className="flex flex-wrap items-center gap-2">
                    {field.options?.map((option) => (
                      <CheckboxCard
                        key={option.id}
                        id={`${field.id}-${option.id}`}
                        checked={(formField.value || []).includes(option.id)}
                        disabled={option.disabled}
                        onCheckedChange={(checked) => {
                          const currentValues = formField.value || []
                          if (checked) {
                            formField.onChange([...currentValues, option.id])
                          } else {
                            formField.onChange(currentValues.filter((id: string) => id !== option.id))
                          }
                        }}
                      >
                        {option.label}
                      </CheckboxCard>
                    ))}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )

      case FieldType.BUTTONS_RADIO:
        return (
          <FormField
            control={control}
            name={field.id}
            render={({ field: formField }) => (
              <FormItem>
                {field.label && <FormLabel isRequired={field.required}>{field.label}</FormLabel>}
                <FormControl>
                  <RadioGroup
                    value={formField.value || ''}
                    onValueChange={formField.onChange}
                    className="flex flex-wrap items-center"
                  >
                    {field.options?.map((option) => (
                      <RadioGroupCard
                        value={option.id}
                        id={`${field.id}-${option.id}`}
                        key={option.id}
                        disabled={option.disabled}
                      >
                        {option.label}
                      </RadioGroupCard>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )

      case FieldType.RADIO:
        return (
          <FormField
            control={control}
            name={field.id}
            render={({ field: formField }) => (
              <FormItem>
                {field.label && <FormLabel isRequired={field.required}>{field.label}</FormLabel>}
                <FormControl>
                  <RadioGroup value={formField.value || ''} onValueChange={formField.onChange} className="space-y-3">
                    {field.options?.map((option) => (
                      <div key={option.id} className="flex items-center space-x-2">
                        <RadioGroupItem
                          value={option.id}
                          id={`${field.id}-${option.id}`}
                          className="border-secondary"
                          disabled={option.disabled}
                        />
                        <Label variant="selection-control" htmlFor={`${field.id}-${option.id}`}>
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )

      case FieldType.SELECT:
        return (
          <FormField
            control={control}
            name={field.id}
            render={({ field: formField }) => (
              <FormItem>
                <FormLabel isRequired={field.required}>{field.label}</FormLabel>
                <Select defaultValue={formField.value} onValueChange={formField.onChange}>
                  <FormControl>
                    <SelectTrigger className="w-full bg-white">
                      <SelectValue placeholder={field.placeholder || t('general.select.placeholder')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {field.options?.map((option) => (
                      <SelectItem key={option.id} value={option.id} disabled={option.disabled}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )

      case FieldType.CHECKBOX:
        return (
          <FormField
            control={control}
            name={field.id}
            render={() => (
              <FormItem>
                {field.options?.map((item) => (
                  <FormField
                    name={field.id}
                    key={item.id}
                    control={control}
                    render={({ field: formField }) => {
                      return (
                        <FormItem key={item.id} className="flex flex-row items-center gap-2">
                          <FormControl>
                            <Checkbox
                              checked={formField.value?.includes(item.id)}
                              disabled={item.disabled}
                              onCheckedChange={(checked) => {
                                return checked
                                  ? formField.onChange([...(formField.value || []), item.id])
                                  : formField.onChange(formField.value?.filter((value: string) => value !== item.id))
                              }}
                            />
                          </FormControl>
                          <FormLabel className="!mt-0 !text-base !font-normal">
                            <Markdown>{item.label}</Markdown>
                          </FormLabel>
                        </FormItem>
                      )
                    }}
                  />
                ))}
                <FormMessage />
              </FormItem>
            )}
          />
        )

      case FieldType.TEXT:
      case FieldType.EMAIL:
      case FieldType.TEL:
      case FieldType.NUMBER:
      case FieldType.PASSWORD:
        return (
          <FormField
            control={control}
            name={field.id}
            render={({ field: formField }) => (
              <FormItem>
                <FormLabel isRequired={field.required}>{field.label}</FormLabel>
                <FormControl>
                  <Input
                    type={field.fieldType}
                    placeholder={field.placeholder}
                    {...formField}
                    autoFocus={autoFocus}
                    readOnly={field.readOnly}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )

      case FieldType.TEXTAREA:
        return (
          <FormField
            control={control}
            name={field.id}
            render={({ field: formField }) => (
              <FormItem>
                <FormLabel isRequired={field.required}>{field.label}</FormLabel>
                <FormControl>
                  <Textarea placeholder={field.placeholder} {...formField} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )

      case FieldType.CONSENT:
        return (
          <FormField
            control={control}
            name={field.id}
            render={({ field: formField }) => (
              <FormItem>
                <div className="flex items-center gap-3">
                  <FormControl>
                    <Checkbox checked={formField.value} onCheckedChange={formField.onChange} />
                  </FormControl>
                  <FormLabel className="!text-xs !font-normal leading-[140%]">
                    <Markdown>{field.label || ''}</Markdown>{' '}
                    <Link href={field.linkUrl || '#'} className="text-primary underline">
                      {field.linkText}
                    </Link>
                  </FormLabel>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        )

      case FieldType.SUBMIT:
        return (
          <Button size="medium" type="submit" loading={field.loading} variant={field.variant as any}>
            {field.label}
          </Button>
        )

      default:
        return null
    }
  }

  return renderField(field)
}
